import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Building2, User, Briefcase } from 'lucide-react';

export default function Login() {
  const [userType, setUserType] = useState(null); // 'organization' or 'candidate'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.login({ email, password, user_type: userType });
      await refreshUser();
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };
  
  if (!userType) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-teal-50 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-4xl">
          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-block">
              <div className="bg-blue-600 text-white font-bold px-6 py-3 text-3xl rounded-lg shadow-lg hover:bg-blue-700 transition-all">
                Symetrix
              </div>
            </Link>
          </div>
          
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-3">Welcome Back</h1>
            <p className="text-lg text-gray-600">Choose how you want to login</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Organization Login */}
            <Card 
              className="border-2 border-transparent hover:border-blue-500 hover:shadow-2xl transition-all cursor-pointer group"
              onClick={() => setUserType('organization')}
              data-testid="organization-login-card"
            >
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                  <Building2 className="text-white" size={40} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">Organization</h2>
                <p className="text-gray-600 mb-6">
                  Login to post jobs, manage applications, and find the best talent for your company
                </p>
                <Button className="bg-blue-600 hover:bg-blue-700 w-full" size="lg">
                  Login as Organization
                </Button>
              </CardContent>
            </Card>

            {/* Candidate Login */}
            <Card 
              className="border-2 border-transparent hover:border-green-500 hover:shadow-2xl transition-all cursor-pointer group"
              onClick={() => setUserType('candidate')}
              data-testid="candidate-login-card"
            >
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                  <User className="text-white" size={40} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">Candidate</h2>
                <p className="text-gray-600 mb-6">
                  Login to browse jobs, apply for positions, and manage your job search
                </p>
                <Button className="bg-green-600 hover:bg-green-700 w-full" size="lg">
                  Login as Candidate
                </Button>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-8">
            <p className="text-gray-600">
              Don't have an account?{' '}
              <Link to="/signup" className="text-blue-600 hover:text-blue-700 font-semibold">
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <div className="bg-blue-600 text-white font-bold px-4 py-2 text-2xl rounded-lg" data-testid="login-logo">
              Symetrix
            </div>
          </Link>
        </div>
        
        <Card className="border-none shadow-xl">
          <CardContent className="p-8">
            {/* Back Button */}
            <button
              onClick={() => setUserType(null)}
              className="text-sm text-gray-600 hover:text-gray-900 mb-6 flex items-center gap-2"
            >
              ← Back to selection
            </button>

            <div className="mb-8">
              <div className="flex items-center gap-3 mb-3">
                {userType === 'organization' ? (
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <Building2 className="text-blue-600" size={24} />
                  </div>
                ) : (
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <User className="text-green-600" size={24} />
                  </div>
                )}
                <div>
                  <h1 className="text-3xl font-bold text-gray-900" data-testid="login-title">
                    {userType === 'organization' ? 'Organization Login' : 'Candidate Login'}
                  </h1>
                </div>
              </div>
              <p className="text-gray-600">
                {userType === 'organization' 
                  ? 'Sign in to manage your job postings and applications'
                  : 'Sign in to browse and apply for jobs'
                }
              </p>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  data-testid="email-input"
                  className="bg-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  data-testid="password-input"
                  className="bg-white"
                />
              </div>
              
              <Button 
                type="submit" 
                className={`w-full ${
                  userType === 'organization' 
                    ? 'bg-blue-600 hover:bg-blue-700' 
                    : 'bg-green-600 hover:bg-green-700'
                }`}
                disabled={loading}
                data-testid="login-button"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
            
            <div className="mt-6 text-center text-sm text-gray-600">
              <p>
                Don't have an account?{' '}
                <Link to="/signup" className="text-blue-600 hover:text-blue-700 font-semibold">
                  Sign up
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
